import React, {useMemo} from 'react';
import {IconText, ProjectIndicator, Table} from 'web-components';
import TotalsLoader, {totalsLoaderQuery} from './TotalsLoader';
import ProjectPortfolioPeriodRow from './ProjectPortfolioPeriodRow';
import ProjectLoader, {projectLoaderQuery} from './ProjectLoader';
import {useIntl} from 'react-intl';
import {filtersToSearchQuery, getPhaseName, getRoleName} from '../ProjectPortfolioReportUtils';
import {ForecastTooltipFormulaRenderer} from '../../../ForecastTooltipFormulaRenderer';
import ForecastTooltip from '../../../../forecast-app/shared/components/tooltips/ForecastTooltip';
import {ProjectPortfolioReportTooltips} from '../ProjectPortfolioReportTooltips';
import PhaseLoader, {phaseLoaderQuery} from './PhaseLoader';
import RoleLoader, {roleLoaderQuery} from './RoleLoader';
import ProjectPortfolioPeriodDownload, {projectPortfolioPeriodDownloadQuery} from './ProjectPortfolioPeriodDownload';
import {PHASE_BREAKDOWN_TYPE} from '../../../../constants';
import {MODAL_TYPE, showModal} from '../../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {LoadMore} from '../../../loaders/LoadMore';

export const ProjectPortfolioPeriodTable = ({
	theEyeOptionsNestedTree,
	filters,
	selectedDateRange: {startDate, endDate},
	setCsvDownloadFunction,
	hasBaseline,
}) => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const pageSize = 100;

	const boolEyeOptions = useMemo(() => {
		const bools = {};
		theEyeOptionsNestedTree.forEach(eyeOption => {
			bools[eyeOption.name] = eyeOption.checked;
			if (eyeOption.nestedOptions) {
				eyeOption.nestedOptions.forEach(nestedOption => {
					bools[nestedOption.name] = nestedOption.checked;
					if (nestedOption.nestedOptions) {
						nestedOption.nestedOptions.forEach(subNestedOption => {
							bools[subNestedOption.name] = subNestedOption.checked;
						});
					}
				});
			}
		});
		return bools;
	}, [theEyeOptionsNestedTree]);

	return (
		<Table paddedCells>
			<Table.Header>
				<Table.HeaderColumn flex={1} visible width="EXTRA_LARGE" />
				{/* Project Overview */}
				<Table.HeaderGrouping name={formatMessage({id: 'project_budget.project_overview'})}>
					<Table.HeaderColumn visible={boolEyeOptions.winProbability} usePadding align="right" width="MEDIUM">
						<ForecastTooltip content={ProjectPortfolioReportTooltips.WinProbabilityTooltip(formatMessage)}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'project_portfolio_report.win_prob'})}
							</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.dates} usePadding align="left" width="SMALL">
						<Table.HeaderColumn.Title>{formatMessage({id: 'common.start'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.dates} usePadding align="left" width="SMALL">
						<Table.HeaderColumn.Title>{formatMessage({id: 'common.end'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.client} usePadding align="left" width="MEDIUM">
						<Table.HeaderColumn.Title>
							{formatMessage({id: 'insights.component.list.column.client'})}
						</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.status} usePadding align="right" width="MEDIUM">
						<Table.HeaderColumn.Title>{formatMessage({id: 'common.status'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.projectOwner} usePadding align="left" width="MEDIUM">
						<Table.HeaderColumn.Title>{formatMessage({id: 'common.project_owner'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.stage} usePadding align="left" width="MEDIUM">
						<Table.HeaderColumn.Title>
							{formatMessage({id: 'insights.component.list.column.status'})}
						</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.allocations} usePadding align="right" width="MEDIUM">
						<Table.HeaderColumn.Title>
							{formatMessage({id: 'scheduling.menu.allocations'})}
						</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.budgetType} usePadding align="left" width="MEDIUM">
						<Table.HeaderColumn.Title>
							{formatMessage({id: 'new_project_modal.budget_type'})}
						</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.rateCard} usePadding align="left" width="LARGE">
						<Table.HeaderColumn.Title>
							{formatMessage({id: 'insights.component.list.column.rateCard'})}
						</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.progress} usePadding align="right" width="MEDIUM">
						<Table.HeaderColumn.Title>{formatMessage({id: 'common.progress'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.progressEom} usePadding align="right" width="MEDIUM">
						<Table.HeaderColumn.Title>
							{formatMessage({id: 'project_portfolio_report.progress_eom'})}
						</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>
				{/* Project Hours */}
				<Table.HeaderGrouping name={formatMessage({id: 'project_portfolio_report.project_hours'})}>
					<Table.HeaderColumn visible={boolEyeOptions.registeredHours} usePadding align="right" width="MEDIUM">
						<ForecastTooltip content={ProjectPortfolioReportTooltips.RegisteredHoursTooltip(formatMessage)}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'project_portfolio_report.registered_hours'})}
							</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.remainingHours} usePadding align="right" width="MEDIUM">
						<ForecastTooltip content={ProjectPortfolioReportTooltips.RemainingHoursTooltip(formatMessage)}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'project_portfolio_report.remaining_hours'})}
							</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.forecastedHours} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.ForecastedHoursTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'project_portfolio_report.forecasted_hours'})}
							</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.totalScope} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.TotalScopeTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'project_portfolio_report.total_scope'})}
							</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.approvedScope} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.ApprovedScopeTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'project_portfolio_report.approved_scope'})}
							</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.baselineHours} usePadding align="right" width="MEDIUM">
						<ForecastTooltip content={ProjectPortfolioReportTooltips.BaselineHoursTooltip(formatMessage)}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'project_portfolio_report.baseline_hours'})}
							</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>
				{/* Baseline */}
				<Table.HeaderGrouping name={formatMessage({id: 'project_section.baseline'})}>
					<Table.HeaderColumn visible={boolEyeOptions.baselineRevenue} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.BaselineRevenueTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'project_budget.revenue'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.baselineCost} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.BaselineCostTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.cost'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.baselineProfit} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.BaselineProfitTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.profit'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.baselineMargin} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.BaselineMarginTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.margin'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>
				{/* Invoiced */}
				<Table.HeaderGrouping name={formatMessage({id: 'common.invoiced'})}>
					<Table.HeaderColumn visible={boolEyeOptions.invoiced} usePadding align="right" width="MEDIUM">
						<Table.HeaderColumn.Title>
							{formatMessage({id: 'project_portfolio_report.invoiced'})}
						</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.paid} usePadding align="right" width="MEDIUM">
						<Table.HeaderColumn.Title>
							{formatMessage({id: 'project_portfolio_report.invoiced_paid'})}
						</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>
				{/* Fixed Price */}
				<Table.HeaderGrouping name={formatMessage({id: 'project_budget.fixed_price'})}>
					<Table.HeaderColumn visible={boolEyeOptions.fixedPriceRevenue} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.ActualRevenueTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'project_budget.revenue'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>
				{/* Actuals */}
				<Table.HeaderGrouping name={formatMessage({id: 'project_budget.actuals'})}>
					<Table.HeaderColumn visible={boolEyeOptions.actualRevenue} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.ActualRevenueTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'project_budget.revenue'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.actualCost} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.ActualCostTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.cost'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.actualProfit} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.ActualProfitTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.profit'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.actualMargin} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.ActualMarginTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.margin'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>
				{/* Plan */}
				<Table.HeaderGrouping name={formatMessage({id: 'common.plan'})}>
					<Table.HeaderColumn visible={boolEyeOptions.planRevenue} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.PlanRevenueTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'project_budget.revenue'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.planCost} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.PlanCostTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.cost'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.planProfit} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.PlanProfitTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.profit'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.planMargin} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.PlanMarginTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.margin'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>
				{/* Remaining */}
				<Table.HeaderGrouping name={formatMessage({id: 'common.remaining'})}>
					<Table.HeaderColumn visible={boolEyeOptions.remainingRevenue} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.RemainingRevenueTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'project_budget.revenue'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.remainingCost} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.RemainingCostTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.cost'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.remainingProfit} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.RemainingProfitTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.profit'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.remainingMargin} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.RemainingMarginTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.margin'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>
				{/* Forecasted */}
				<Table.HeaderGrouping name={formatMessage({id: 'common.forecasted'})}>
					<Table.HeaderColumn visible={boolEyeOptions.forecastedRevenue} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.ForecastRevenueTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'project_budget.revenue'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.forecastedCost} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.ForecastCostTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.cost'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.forecastedProfit} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.ForecastProfitTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.profit'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.forecastedMargin} usePadding align="right" width="MEDIUM">
						<ForecastTooltip
							maxWidth="none"
							content={
								<ForecastTooltipFormulaRenderer
									items={ProjectPortfolioReportTooltips.ForecastMarginTooltip(formatMessage)}
									translatedMessage={true}
								/>
							}
						>
							<Table.HeaderColumn.Title>{formatMessage({id: 'common.margin'})}</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>
				{/* Actual vs. Plan */}
				<Table.HeaderGrouping name={formatMessage({id: 'project_budget.actual_vs_plan'})}>
					<Table.HeaderColumn visible={boolEyeOptions.actualPlanRevenue} usePadding align="right" width="MEDIUM">
						<Table.HeaderColumn.Title>{formatMessage({id: 'project_budget.revenue'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.actualPlanCost} usePadding align="right" width="MEDIUM">
						<Table.HeaderColumn.Title>{formatMessage({id: 'common.cost'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.actualPlanProfit} usePadding align="right" width="MEDIUM">
						<Table.HeaderColumn.Title>{formatMessage({id: 'common.profit'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>
				{/* Forecasted vs. Plan */}
				<Table.HeaderGrouping name={formatMessage({id: 'project_budget.forecast_vs_plan'})}>
					<Table.HeaderColumn visible={boolEyeOptions.forecastedPlanRevenue} usePadding align="right" width="MEDIUM">
						<Table.HeaderColumn.Title>{formatMessage({id: 'project_budget.revenue'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.forecastedPlanCost} usePadding align="right" width="MEDIUM">
						<Table.HeaderColumn.Title>{formatMessage({id: 'common.cost'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.forecastedPlanProfit} usePadding align="right" width="MEDIUM">
						<Table.HeaderColumn.Title>{formatMessage({id: 'common.profit'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>
				{/* Invoice vs. Actual */}
				<Table.HeaderGrouping name={formatMessage({id: 'project_budget.invoice_vs_actual'})}>
					<Table.HeaderColumn visible={boolEyeOptions.invoicedActualInvoiced} usePadding align="right" width="MEDIUM">
						<Table.HeaderColumn.Title>{formatMessage({id: 'project_budget.invoiced'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.invoicedActualRevenue} usePadding align="right" width="MEDIUM">
						<Table.HeaderColumn.Title>{formatMessage({id: 'project_budget.revenue'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.invoicedActualAccrued} usePadding align="right" width="MEDIUM">
						<Table.HeaderColumn.Title>{formatMessage({id: 'project_budget.accrued'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>
				{/* Labels */}
				<Table.HeaderGrouping name={formatMessage({id: 'common.labels'})}>
					<Table.HeaderColumn visible={boolEyeOptions.labels} usePadding align="center" width="EXTRA_LARGE">
						<Table.HeaderColumn.Title>{formatMessage({id: 'common.labels'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>
				{/* Retainer period target */}
				<Table.HeaderGrouping name={formatMessage({id: 'project_budget.retainer_period_target'})}>
					<Table.HeaderColumn
						visible={boolEyeOptions.retainerPeriodTargetMinutes}
						usePadding
						align="right"
						width="MEDIUM"
					>
						<Table.HeaderColumn.Title>
							{formatMessage({id: 'project_budget.retainer_period_target_minutes'})}
						</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn
						visible={boolEyeOptions.retainerPeriodTargetPrice}
						usePadding
						align="right"
						width="MEDIUM"
					>
						<Table.HeaderColumn.Title>
							{formatMessage({id: 'project_budget.retainer_period_target_price'})}
						</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>
			</Table.Header>
			<LoadMore
				key="query-render-totals"
				query={totalsLoaderQuery}
				variables={{
					searchQuery: filtersToSearchQuery(filters),
					startYear: startDate.year(),
					startMonth: startDate.month() + 1,
					startDay: startDate.date(),
					endYear: endDate.year(),
					endMonth: endDate.month() + 1,
					endDay: endDate.date(),
				}}
				componentName="project_portfolio_period_totals_loader"
				loader={<TotalsLoader />}
			>
				{({data}) => {
					return (
						<Table.Rows data={data} canExpand={false} hasHeader>
							{({rowData, tableColumnsProps}) => {
								return (
									<ProjectPortfolioPeriodRow
										rowData={rowData}
										tableColumnProps={tableColumnsProps}
										headerColumn={<Table.Column>Totals</Table.Column>}
									/>
								);
							}}
						</Table.Rows>
					);
				}}
			</LoadMore>
			<LoadMore
				key="query-render-project"
				query={projectLoaderQuery}
				variables={{
					searchQuery: filtersToSearchQuery(filters),
					startYear: startDate.year(),
					startMonth: startDate.month() + 1,
					startDay: startDate.date(),
					endYear: endDate.year(),
					endMonth: endDate.month() + 1,
					endDay: endDate.date(),
					pageSize: pageSize,
				}}
				componentName="project_portfolio_period_project_loader"
				loader={<ProjectLoader />}
			>
				{({data, loadMore}) => {
					setCsvDownloadFunction(() => {
						showModal({
							type: MODAL_TYPE.DOWNLOADING_CSV_DATA,
							componentName: 'project_portfolio_period_project_csv_loader',
							query: projectPortfolioPeriodDownloadQuery,
							variables: {
								searchQuery: filtersToSearchQuery(filters),
								startYear: startDate.year(),
								startMonth: startDate.month() + 1,
								startDay: startDate.date(),
								endYear: endDate.year(),
								endMonth: endDate.month() + 1,
								endDay: endDate.date(),
								pageSize: 1000000,
							},
							createCsvDownload: <ProjectPortfolioPeriodDownload />,
							startDate,
							endDate,
							boolEyeOptions,
						});
					});
					return (
						<Table.Rows data={data} canExpand={true} loadMore={loadMore} autoload={true}>
							{({rowData, tableColumnsProps, expanded, nextLevelProps: phaseLevelProps}) => {
								const fullAccessToProject = rowData?.project?.fullAccessToProject;
								return (
									<>
										<ProjectPortfolioPeriodRow
											rowData={rowData}
											tableColumnProps={tableColumnsProps}
											headerColumn={
												<IconText
													key={rowData.project.id}
													text={
														fullAccessToProject
															? rowData.project.name
															: intl.formatMessage({id: 'common.no_project_access'})
													}
												>
													<ProjectIndicator
														project={rowData.project}
														name={rowData.project.name}
														id={rowData.project.companyProjectId}
														color={rowData.project.projectColor}
														noTitle={!fullAccessToProject}
													/>
												</IconText>
											}
											hasBaseline={hasBaseline}
										/>
										{expanded && (
											<LoadMore
												key="query-render-phase"
												query={phaseLoaderQuery}
												componentName="project_portfolio_period_phase_loader"
												loader={<PhaseLoader />}
												variables={{
													projectId: rowData.project.id,
													startYear: startDate.year(),
													startMonth: startDate.month() + 1,
													startDay: startDate.date(),
													endYear: endDate.year(),
													endMonth: endDate.month() + 1,
													endDay: endDate.date(),
												}}
											>
												{({data: phaseBreakdownData}) => {
													return (
														<Table.Rows
															data={phaseBreakdownData}
															{...phaseLevelProps}
															canExpand={true}
														>
															{({
																rowData: phaseRowData,
																tableColumnsProps: phaseTableColumnProps,
																expanded: phaseExpanded,
																nextLevelProps: roleLevelProps,
															}) => {
																return (
																	<>
																		<ProjectPortfolioPeriodRow
																			rowData={phaseRowData}
																			tableColumnProps={phaseTableColumnProps}
																			headerColumn={getPhaseName(intl, phaseRowData)}
																			canExpand={
																				(phaseRowData.breakdownType ===
																					PHASE_BREAKDOWN_TYPE.PHASE &&
																					phaseRowData.phase.id) ||
																				phaseRowData.breakdownType ===
																					PHASE_BREAKDOWN_TYPE.NO_PHASE
																			}
																		/>
																		{phaseExpanded && (
																			<LoadMore
																				key="query-render-role"
																				query={roleLoaderQuery}
																				componentName="project_portfolio_period_role_loader"
																				loader={<RoleLoader />}
																				variables={{
																					projectId: rowData.project.id,
																					phaseId:
																						phaseRowData.breakdownType ===
																							PHASE_BREAKDOWN_TYPE.PHASE &&
																						phaseRowData.phase.id
																							? phaseRowData.phase.id
																							: phaseRowData.breakdownType ===
																							  PHASE_BREAKDOWN_TYPE.NO_PHASE
																							? 0
																							: null,
																					startYear: startDate.year(),
																					startMonth: startDate.month() + 1,
																					startDay: startDate.date(),
																					endYear: endDate.year(),
																					endMonth: endDate.month() + 1,
																					endDay: endDate.date(),
																				}}
																			>
																				{({data: roleBreakdownData}) => {
																					return (
																						<Table.Rows
																							data={roleBreakdownData}
																							{...roleLevelProps}
																							canExpand={false}
																						>
																							{({
																								rowData: roleRowData,
																								tableColumnsProps:
																									roleTableColumnProps,
																							}) => {
																								return (
																									<ProjectPortfolioPeriodRow
																										rowData={roleRowData}
																										tableColumnProps={
																											roleTableColumnProps
																										}
																										headerColumn={getRoleName(
																											intl,
																											roleRowData
																										)}
																									/>
																								);
																							}}
																						</Table.Rows>
																					);
																				}}
																			</LoadMore>
																		)}
																	</>
																);
															}}
														</Table.Rows>
													);
												}}
											</LoadMore>
										)}
									</>
								);
							}}
						</Table.Rows>
					);
				}}
			</LoadMore>
		</Table>
	);
};
